/**
 * @file api/tauri.ts
 * @description Tauri-specific file API implementation.
 */

import { AppFileAPI } from '@hcie/core';
import { imageFormatRegistry } from '../format-registry';

export function buildTauriApi(): AppFileAPI {
  const { open, save } = (window as any).__TAURI__!.dialog;
  const { readTextFile, readFile, writeTextFile, writeFile } = (window as any).__TAURI__!.fs;

  return {
    openFile: async (): Promise<string | null> => {
      const rawFilter = imageFormatRegistry.getOpenFilter();
      const tauriFilters = rawFilter.split("|").reduce((acc: any[], curr: string, i: number, arr: string[]) => {
          if (i % 2 === 0 && curr.trim() !== "") {
              const filterExtensions = arr[i+1] || "";
              const extensions = filterExtensions
                  .split(";")
                  .map(e => e.replace(/\*\./g, "").trim())
                  .filter(e => e !== "");
              
              if (extensions.length > 0) {
                  acc.push({ name: curr, extensions });
              }
          }
          return acc;
      }, []);

      const result = await open({ filters: tauriFilters });
      if (Array.isArray(result)) return result[0] ?? null;
      return result;
    },

    readFile: async (filePath: string): Promise<string | null> => readTextFile(filePath),

    readFileBinary: async (filePath: string): Promise<Uint8Array | null> => readFile(filePath),

    saveFile: async (
      content: string | Uint8Array | ArrayBuffer,
      filePath: string | null,
      saveas: boolean,
      type: string
    ): Promise<string | null> => {
      try {
        let filters: { name: string; extensions: string[] }[] = [];
        if (type === 'hcie') {
            filters = [{ name: 'HC Image Editor Project', extensions: ['hcie'] }];
        } else {
            const extWithDot = type.startsWith('.') ? type : '.' + type;
            const format = imageFormatRegistry.getByExtension(extWithDot);
            if (format) {
                filters = [{ name: format.name, extensions: format.extensions.map((e: string) => e.replace(".", "")) }];
            } else {
                filters = [{ name: type.toUpperCase(), extensions: [type.replace(".", "")] }];
            }
        }

        let targetPath = filePath;
        const suggestion = filePath ?? `untitled.${type.replace(".", "")}`;
        
        if (saveas || !filePath) {
          targetPath = await save({ 
            filters, 
            defaultPath: suggestion
          });
          if (!targetPath) return null;
        }
        if (!targetPath) return null;

        const expectedExt = type.startsWith('.') ? type : '.' + type;
        const endsWithExpected = targetPath.toLowerCase().endsWith(expectedExt.toLowerCase());
        
        // Special case for .jpg/.jpeg
        const isJpegMatch = (type === 'jpg' || type === '.jpg') && targetPath.toLowerCase().endsWith('.jpeg');
        
        if (!endsWithExpected && !isJpegMatch) {
            targetPath += expectedExt;
        }

        if (type === 'hcie') {
          await writeTextFile(targetPath, content as string);
        } else {
          let bytes: Uint8Array;
          if (typeof content === 'string' && content.startsWith('data:')) {
            const base64 = content.replace(/^data:image\/\w+;base64,/, '');
            const binary = atob(base64);
            bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          } else if (content instanceof Uint8Array) {
            bytes = content;
          } else if (content instanceof ArrayBuffer) {
            bytes = new Uint8Array(content);
          } else {
            bytes = new TextEncoder().encode(content as string);
          }
          await writeFile(targetPath, bytes);
        }
        return targetPath;
      } catch (err) {
        console.error('[Tauri] saveFile failed:', err);
        throw err;
      }
    },

    onMenuOpen: () => {},
    onMenuSave: () => {},
    onMenuSaveAs: () => {},
    onMenuExport: () => {},
    onMenuErodeBorder: () => {},
    onMenuFadeBorder: () => {},
    isDesktop: true
  };
}
